import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAdminApi } from "./useAdminApi";
import ImageLightbox from "./ImageLightbox";
import IgdbPlatformPickerModal from "./IgdbPlatformPickerModal";
import CoverCurationModal, { CURATION_STATUS_LABELS } from "./CoverCurationModal";

const PAGE_SIZE = 20;
const COVER_FILTERS = ["all", "pending", "approved", "curated", "not_cover", "manual", "no_aspect"];
const LINK_FILTERS = [
  { key: "all", label: "All" },
  { key: "platform", label: "Automatic" },
  { key: "manual", label: "Manual" },
  { key: "clone", label: "Clones" },
  // Undecided, or decided as "no IGDB platform" for a non-clone.
  { key: "unlinked", label: "No platform" },
];

// How a console's IGDB platform was decided — see consolePlatform.ts in the API.
const LINK_SOURCE_LABELS = {
  platform: "automatic",
  manual: "manual",
  clone: "clone — no platform",
};

// Manufacturer prefixes off a LaunchBox platform name, to prefill the IGDB search with
// something IGDB's names contain ("Nintendo 3DS" → "3DS" finds both 3DS and New 3DS).
function searchSeed(platformName) {
  return (platformName || "").replace(/^(Sony|Microsoft|Nintendo|Sega|SNK|NEC|Atari)\s+/i, "");
}

function PlatformSummary({ item }) {
  const link = item.igdb_platform_link;
  const sourceLabel = link && LINK_SOURCE_LABELS[link.source];
  return (
    <div className="admin-source-meta">
      IGDB:{" "}
      {link?.igdb_platform
        ? <strong>{link.igdb_platform.name} ({link.igdb_platform.id})</strong>
        : <strong>{link ? "none" : item.auto_lookup_name ? `pending — auto lookup "${item.auto_lookup_name}"` : "not found — link it"}</strong>}
      {sourceLabel && <span className={`admin-console-source admin-console-source--${link.source}`}>{sourceLabel}</span>}
    </div>
  );
}

// The LaunchBox consoles the app has opened, newest first: the IGDB platform each was
// linked to on its first open, and its cover review. A console enters with its cover
// unverified (the app renders it flat) and its platform decided automatically.
function ConsolesTab({ user, onForbidden }) {
  const apiFetch = useAdminApi(user, onForbidden);
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState(null);
  const [nameInput, setNameInput] = useState("");
  const [name, setName] = useState("");
  const [coverFilter, setCoverFilter] = useState("all");
  const [linkFilter, setLinkFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [picking, setPicking] = useState(null); // row being linked
  const [curating, setCurating] = useState(null); // launchbox_id
  const dirtyRef = useRef(false);

  useEffect(() => {
    const handle = setTimeout(() => {
      setName(nameInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(handle);
  }, [nameInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ cover: coverFilter, link: linkFilter, page: String(page), page_size: String(PAGE_SIZE) });
      if (name) params.set("q", name);
      const data = await apiFetch(`/admin/consoles?${params.toString()}`);
      setItems(data.items);
      setCounts(data.counts);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      if (err.message !== "Forbidden") setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, coverFilter, linkFilter, name, page]);

  useEffect(() => {
    load();
  }, [load]);

  const closePicker = useCallback(() => setPicking(null), []);

  // The list reloads once the cover modal closes, so a reviewed row doesn't vanish mid-edit.
  const closeCuration = useCallback(() => {
    setCurating(null);
    if (dirtyRef.current) load();
    dirtyRef.current = false;
  }, [load]);
  const markDirty = useCallback(() => {
    dirtyRef.current = true;
  }, []);

  async function savePlatform(platform) {
    await apiFetch(`/admin/consoles/${picking.launchbox_id}/igdb-platform`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ igdb_platform: platform, scope: "model" }),
    });
    load();
  }

  async function resetPlatform(item) {
    if (!window.confirm(`Go back to the automatic IGDB platform for every version of "${item.name}"?`)) return;
    try {
      await apiFetch(`/admin/consoles/${item.launchbox_id}/igdb-platform?scope=model`, { method: "DELETE" });
      load();
    } catch (err) {
      if (err.message !== "Forbidden") window.alert("Couldn't reset the IGDB platform");
    }
  }

  function filterButtons(options, value, setValue, countsFor) {
    return (
      <div className="admin-linked-filter">
        {options.map((option) => (
          <button
            key={option.key}
            type="button"
            className={`admin-tab admin-linked-filter-btn ${value === option.key ? "admin-tab-active" : ""}`}
            onClick={() => {
              setValue(option.key);
              setPage(1);
            }}
          >
            {option.label}
            {countsFor && ` · ${countsFor[option.key]}`}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="admin-console-filters">
        <span className="admin-source-meta">Cover</span>
        {filterButtons(
          COVER_FILTERS.map((key) => ({ key, label: key === "all" ? "All" : key === "no_aspect" ? "Crop without aspect" : CURATION_STATUS_LABELS[key] })),
          coverFilter,
          setCoverFilter,
          counts?.cover,
        )}
        <span className="admin-source-meta">IGDB</span>
        {filterButtons(LINK_FILTERS, linkFilter, setLinkFilter, counts?.link)}
      </div>
      <div className="admin-links-search">
        <input
          type="text"
          placeholder="Filter by name or platform…"
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          className="admin-tgdb-link-input admin-links-search-input"
        />
      </div>

      {loading && <p className="admin-status">Loading…</p>}
      {error && <p className="admin-status admin-error">{error}</p>}

      <div className="admin-table glass">
        {items && items.length === 0 && (
          <p className="admin-status">
            {counts?.cover.all === 0 ? "No consoles yet. They show up here once someone opens them in the app." : "No consoles match these filters."}
          </p>
        )}
        {items && items.map((item) => (
          <div className="admin-row admin-row--covers" key={item.launchbox_id}>
            <div className="admin-boxart-sides admin-boxart-sides--row">
              {item.cover_url
                ? (
                  <img
                    className="admin-boxart admin-boxart--row admin-boxart-clickable"
                    src={item.cover_url}
                    alt={item.name}
                    onClick={() => setLightbox({ src: item.cover_url, alt: item.name })}
                  />
                )
                : <div className="admin-boxart-placeholder admin-boxart-placeholder--row">No art</div>}
            </div>
            <div className="admin-row-main">
              <div className="admin-source-name">
                {item.name}
                {item.release_type !== "official" && (
                  <code className="admin-barcode-chip admin-platform-abbr">{item.release_type}</code>
                )}
              </div>
              <div className="admin-source-meta">
                {item.platform_name} · {item.country_name || "Unknown country"} · launchbox_id {item.launchbox_id}
              </div>
              <PlatformSummary item={item} />
              <div className="admin-source-meta">
                Cover:
                <span className={`admin-curation-status admin-curation-status--${item.cover_status}`}>
                  {CURATION_STATUS_LABELS[item.cover_status]}
                </span>
                {item.face_count > 0 && ` ${item.face_count} own image${item.face_count === 1 ? "" : "s"}`}
                {item.no_aspect && <span className="admin-error"> · crop without aspect</span>}
              </div>
            </div>
            <div className="admin-row-actions admin-row-actions--gap">
              <button type="button" className="admin-signout-btn" onClick={() => setPicking(item)}>
                {item.igdb_platform_link?.igdb_platform ? "Change" : "Link"}
              </button>
              {item.igdb_platform_link?.source === "manual" && (
                <button type="button" className="admin-signout-btn admin-delete-btn" onClick={() => resetPlatform(item)}>
                  Automatic
                </button>
              )}
              <button type="button" className="admin-signout-btn" onClick={() => setCurating(item.launchbox_id)}>
                Cover
              </button>
            </div>
          </div>
        ))}
      </div>

      {items && items.length > 0 && (
        <div className="admin-pagination">
          <button
            type="button"
            className="admin-signout-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            Previous
          </button>
          <span className="admin-pagination-status">
            Page {page} of {totalPages} · {total} consoles
          </span>
          <button
            type="button"
            className="admin-signout-btn"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
          >
            Next
          </button>
        </div>
      )}

      <ImageLightbox src={lightbox?.src} alt={lightbox?.alt} onClose={() => setLightbox(null)} />
      {picking && (
        <IgdbPlatformPickerModal
          title="IGDB platform"
          subtitle={`${picking.name} — every version of this model`}
          initialQuery={searchSeed(picking.platform_name)}
          allowNone
          apiFetch={apiFetch}
          onPick={savePlatform}
          onClose={closePicker}
        />
      )}
      {curating && (
        <CoverCurationModal launchboxId={curating} apiFetch={apiFetch} onClose={closeCuration} onChanged={markDirty} />
      )}
    </div>
  );
}

export default ConsolesTab;
